import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ArrowRight, Briefcase, ChevronRight, Clock, MapPin,
  User as UserIcon, CheckCircle2, Loader,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import AddInterviewModal from "../components/AddInterviewModal";

/**
 * Interviews · "Interview Mode" — lists upcoming + past interviews, lets users
 * schedule a new one, and links into each interview's prep page.
 */
export default function Interviews() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/interviews");
      setUpcoming(data.upcoming || []);
      setPast(data.past || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load interviews.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreated = (iv) => {
    setShowAdd(false);
    setUpcoming((prev) => [iv, ...prev].sort((a, b) => a.interview_at.localeCompare(b.interview_at)));
    navigate(`/interviews/${iv.id}`);
  };

  return (
    <div className="min-h-screen rm-paper" data-testid="interviews-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-1"
                 style={{ fontWeight: 700 }}>
              · New · Interview Mode
            </div>
            <h1 className="font-display uppercase leading-none mb-2"
                style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                data-testid="interviews-title">
              Upcoming <span className="rm-text-orange">interviews.</span>
            </h1>
            <p className="text-sm md:text-base max-w-2xl" style={{ color: "#334155" }}>
              Schedule an interview, and Resmo bundles your Strategy Brief,
              Playbook, and Interviewer Research into a 5-minute briefing —
              with audio playback and calendar reminders at 7 days, 24 hours,
              and 1 hour before.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="rm-btn rm-btn-primary text-sm px-4 py-2.5"
            data-testid="interviews-add-btn"
          >
            <Plus size={14} /> Schedule interview
          </button>
        </div>

        {error && (
          <div className="rm-card-static p-3 mb-4 text-xs font-mono-rm"
               style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
               data-testid="interviews-error">
            · {error}
          </div>
        )}

        {loading ? (
          <div className="rm-card-static p-6 flex items-center gap-2"
               style={{ background: "#FFFBEB", borderColor: "#FFC145" }}>
            <Loader size={16} className="animate-spin" style={{ color: "#FF6A00" }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading…</span>
          </div>
        ) : (
          <>
            <Section
              title="Upcoming"
              testid="interviews-upcoming-section"
              empty="No upcoming interviews. Schedule your first one to lock in the briefing."
              items={upcoming}
              onClick={(iv) => navigate(`/interviews/${iv.id}`)}
              accent="#FF6A00"
            />

            {past.length > 0 && (
              <Section
                title="Recent"
                testid="interviews-past-section"
                items={past}
                onClick={(iv) => navigate(`/interviews/${iv.id}`)}
                accent="#475569"
                muted
              />
            )}
          </>
        )}
      </div>

      <AddInterviewModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}

function Section({ title, items, onClick, accent, testid, empty, muted = false }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-3"
           style={{ color: accent, fontWeight: 700 }}>
        · {title} · {items.length}
      </div>
      {items.length === 0 && empty ? (
        <div className="rm-card-static p-4 text-sm"
             style={{ background: "white", borderColor: "#CBD5E1", color: "#475569" }}
             data-testid={`${testid}-empty`}>
          {empty}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((iv) => (
            <InterviewCard key={iv.id} iv={iv} onClick={() => onClick(iv)} muted={muted} />
          ))}
        </div>
      )}
    </section>
  );
}

function InterviewCard({ iv, onClick, muted }) {
  const when = useMemo(() => new Date(iv.interview_at), [iv.interview_at]);
  const dateLabel = when.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
  const countdownDays = Math.max(0, Math.floor((when.getTime() - Date.now()) / (24 * 3600 * 1000)));
  const countdownHours = Math.max(0, Math.floor((when.getTime() - Date.now()) / 3600000));
  const isPast = when.getTime() < Date.now();
  const isImminent = !isPast && countdownHours < 24;
  return (
    <button
      type="button"
      onClick={onClick}
      className="rm-card-static p-4 text-left w-full"
      style={{
        background: muted ? "#F8FAFC" : (isImminent ? "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)" : "white"),
        borderColor: "#0F1B2D",
        boxShadow: muted ? "2px 2px 0 0 #94A3B8" : (isImminent ? "4px 4px 0 0 #B45309" : "3px 3px 0 0 #FF6A00"),
        opacity: muted ? 0.85 : 1,
      }}
      data-testid={`interview-card-${iv.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 flex flex-col items-center justify-center text-center"
             style={{
               width: 56, height: 56, borderRadius: 8,
               background: isImminent ? "#B45309" : "#0F1B2D",
               color: isImminent ? "#FFFBEB" : "#FFC145",
               border: "2px solid #0F1B2D",
             }}>
          {isPast ? (
            <CheckCircle2 size={22} />
          ) : countdownDays > 0 ? (
            <>
              <div className="font-display text-xl leading-none">{countdownDays}</div>
              <div className="font-mono-rm text-[8px] uppercase tracking-widest mt-0.5">
                day{countdownDays === 1 ? "" : "s"}
              </div>
            </>
          ) : (
            <>
              <div className="font-display text-xl leading-none">{countdownHours}</div>
              <div className="font-mono-rm text-[8px] uppercase tracking-widest mt-0.5">
                hr{countdownHours === 1 ? "" : "s"}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-display uppercase text-base leading-tight mb-1"
               style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
            {iv.role_title}
          </div>
          <div className="font-mono-rm text-[11px] uppercase tracking-widest inline-flex items-center gap-1"
               style={{ color: "#475569" }}>
            <Briefcase size={11} /> {iv.role_company}
          </div>
          <div className="text-[12.5px] mt-1.5 flex items-center gap-1.5" style={{ color: "#0F1B2D" }}>
            <Clock size={11} style={{ color: "#FF6A00" }} /> {dateLabel}
          </div>
          {iv.interviewer_name && (
            <div className="text-[12px] mt-1 flex items-center gap-1.5" style={{ color: "#475569" }}>
              <UserIcon size={11} /> With {iv.interviewer_name}
            </div>
          )}
          {iv.location && (
            <div className="text-[11px] mt-1 flex items-center gap-1.5 truncate" style={{ color: "#94A3B8" }}>
              <MapPin size={10} /> {iv.location}
            </div>
          )}
        </div>

        <ChevronRight size={18} style={{ color: "#94A3B8", flexShrink: 0 }} />
      </div>
    </button>
  );
}
